import { ApiError } from '@/services/api';
import {
  type AsesoriaDetailResponse,
  type AudioProcessingStatusResponse,
  AsesoriaInfoResponse,
  asesoriasService,
} from '@/services/asesorias.service';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseAsesoriaActivaResult {
  data: AsesoriaInfoResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

//AsesoriaInfoResponse

interface UseAsesoriaResult {
  data: AsesoriaDetailResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseAudioProcessingStatusResult {
  data: AudioProcessingStatusResponse | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  isNotFound: boolean;
  refetch: () => Promise<AudioProcessingStatusResponse | null>;
}

const AUDIO_STATUS_POLL_INTERVAL_MS = 15000;
const AUDIO_STATUS_CONFIRM_STOP_DELAY_MS = 2000;
const AUDIO_STATUS_BACKOFF_INITIAL_MS = 5000;
const AUDIO_STATUS_BACKOFF_MAX_MS = 60000;
export function useAsesoria(asesoriaId: number, nit: string): UseAsesoriaActivaResult {
  const [data, setData] = useState<AsesoriaInfoResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAsesoria = useCallback(async () => {
    if (!asesoriaId || !nit) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await asesoriasService.getById(asesoriaId, nit);
      if (response.success) setData(response.asesoria);
      else setError('Error al cargar asesoría');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar asesoría');
    } finally {
      setIsLoading(false);
    }
  }, [asesoriaId, nit]);

  useEffect(() => {
    fetchAsesoria();
  }, [fetchAsesoria]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchAsesoria,
  };
}
export function useAsesoriaById(nit: string, asesoriaId: string | undefined): UseAsesoriaResult {
  const [data, setData] = useState<AsesoriaDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAsesoria = useCallback(async () => {
    if (!nit || !asesoriaId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await asesoriasService.getById(Number(asesoriaId), nit);
      if (response.success) setData(response.asesoria);
      else setError('Error al cargar asesoría');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar asesoría');
    } finally {
      setIsLoading(false);
    }
  }, [nit, asesoriaId]);

  useEffect(() => {
    fetchAsesoria();
  }, [fetchAsesoria]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchAsesoria,
  };
}

export function useAsesoriaActiva(nit: string, enabled: boolean = true): UseAsesoriaResult {
  const [data, setData] = useState<AsesoriaDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAsesoriaActiva = useCallback(async () => {
    if (!enabled || !nit) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await asesoriasService.getActiva(nit);
      if (response.success) setData(response.asesoria);
      else setError('Error al cargar asesoría activa');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar asesoría activa');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, nit]);

  useEffect(() => {
    fetchAsesoriaActiva();
  }, [fetchAsesoriaActiva]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchAsesoriaActiva,
  };
}

export function useAudioProcessingStatus(nit: string, enabled: boolean = true): UseAudioProcessingStatusResult {
  const [data, setData] = useState<AudioProcessingStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNotFound, setIsNotFound] = useState(false);
  const [pollTick, setPollTick] = useState(0);
  const retryDelayMsRef = useRef(AUDIO_STATUS_BACKOFF_INITIAL_MS);
  const forceStopConfirmationRef = useRef(false);
  const hasLoadedOnceRef = useRef(false);

  useEffect(() => {
    setData(null);
    setError(null);
    setIsNotFound(false);
    setPollTick(0);
    setIsRefreshing(false);
    setIsLoading(Boolean(enabled && nit));
    retryDelayMsRef.current = AUDIO_STATUS_BACKOFF_INITIAL_MS;
    forceStopConfirmationRef.current = false;
    hasLoadedOnceRef.current = false;
  }, [enabled, nit]);

  const fetchStatus = useCallback(
    async (isManualRefetch: boolean = false): Promise<AudioProcessingStatusResponse | null> => {
      if (!nit) {
        setIsLoading(false);
        return null;
      }

      if (!enabled && !isManualRefetch) {
        setIsLoading(false);
        return null;
      }

      if (isManualRefetch) {
        setIsRefreshing(true);
      } else if (!hasLoadedOnceRef.current) {
        setIsLoading(true);
      }

      try {
        const response = await asesoriasService.getAudioProcessingStatus(nit);

        setData(response);
        setError(null);
        setIsNotFound(false);
        retryDelayMsRef.current = AUDIO_STATUS_BACKOFF_INITIAL_MS;

        return response;
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          setData(null);
          setIsNotFound(true);
          setError(err.message || 'Asesoría activa no encontrada');
          return null;
        }

        setError(err instanceof Error ? err.message : 'Error al consultar estado de audios');
        setIsNotFound(false);
        return null;
      } finally {
        hasLoadedOnceRef.current = true;
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [enabled, nit]
  );

  useEffect(() => {
    if (!enabled || !nit || isNotFound) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const schedule = (delayMs: number) => {
      timeoutId = setTimeout(() => setPollTick((v) => v + 1), delayMs);
    };

    const run = async () => {
      const response = await fetchStatus();
      if (!response) {
        schedule(retryDelayMsRef.current);
        retryDelayMsRef.current = Math.min(retryDelayMsRef.current * 2, AUDIO_STATUS_BACKOFF_MAX_MS);
        return;
      }

      if (response.hasAudiosEnProcesamiento) {
        forceStopConfirmationRef.current = false;
        schedule(AUDIO_STATUS_POLL_INTERVAL_MS);
        return;
      }

      if (!forceStopConfirmationRef.current) {
        forceStopConfirmationRef.current = true;
        schedule(AUDIO_STATUS_CONFIRM_STOP_DELAY_MS);
      }
    };

    void run();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [enabled, nit, pollTick, fetchStatus, isNotFound]);

  const refetch = useCallback(async (): Promise<AudioProcessingStatusResponse | null> => {
    const response = await fetchStatus(true);
    if (response?.hasAudiosEnProcesamiento) {
      forceStopConfirmationRef.current = false;
      retryDelayMsRef.current = AUDIO_STATUS_BACKOFF_INITIAL_MS;
    }
    return response;
  }, [fetchStatus]);

  return {
    data,
    isLoading,
    isRefreshing,
    error,
    isNotFound,
    refetch,
  };
}

export function useCancelarVerificacion() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancelar = useCallback(async (nit: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await asesoriasService.cancelarVerificacion(nit);
      return response.success;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cancelar verificación');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { cancelar, isLoading, error };
}
